import { Component, signal, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { DatePipe, NgIf } from "@angular/common";
import { AuthService } from "../service/auth.service";
import { AnalyticsService } from "../service/analytics.service";
import { GeneralService } from "../service/general.service";
import { ValidationService } from "../service/validation.service";
import { AbstractBuyerComponent } from "./abstract/buyer.component";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { AITask } from "../entity/AITask";
import { first, firstValueFrom } from "rxjs";
import { Account } from "../entity/Account";
import { AccountData } from "../obj/AccountData";
import { IConsumptionDialog } from "./incs/i-dialog-consumption.component";
import { HeaderComponent } from "./incs/header.component";

@Component({
    selector: "kilowant-ai-task-list",
    templateUrl: "../../html/main-ai-task-list.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [HeaderComponent, NgIf, DatePipe]
})
export class AITaskListComponent extends AbstractBuyerComponent {
    tasksData: any[] = [];
    total: number = 0;
    tabIndex: number = 0;
    activeTasks: AITask[] = [];
    accountsNotInAI: AccountData[] = [];
    showAllTasks: boolean[] = [];
    selectedAccounts: number[][] = [];
    selectedAll: boolean[] = [];

    viewSubscription: boolean[] = [];
    ifAnyTaskActive = signal<boolean>(false);

    displayedColumns: string[] = ["TaskName", "StartDate", "Account"];
    inactiveDisplayedColumns: string[] = ["TaskName", "Account"];
    iConsumptionDialogRef: MatDialogRef<IConsumptionDialog> = null!;
    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        protected datepipe: DatePipe,
        dialog: MatDialog
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        this.eventSubscriptions.push(this.generalService.changeClientActivities$.subscribe(
            client => {
                this.pageStart();
            })
        );
        super.ngOnInit();
    }

    pageStart(): void {
        this.refresh();
    }

    tabChanged(tabChangeEvent: MatTabChangeEvent): void {
        this.tabIndex = tabChangeEvent.index;
    }

    refresh(): void {
        this.selectedAll = [];
        this.showAllTasks = [];
        this.selectedAccounts = [];
        this.ifAnyTaskActive.set(false);

        this.generalService.getAITasks().pipe(first()).subscribe((data: any) => {
            this.activeTasks = [];
            for (let i = 0; i < data.length; i++) {
                this.showAllTasks.push(false);
                this.showAllTasks.push(false);
                this.selectedAccounts.push([]);
                this.selectedAccounts.push([]);
                this.selectedAll.push(false);
                this.selectedAll.push(false);
                this.activeTasks.push(data[i]);
                if (data[i].accounts.length > 0) {
                    this.viewSubscription[i] = true;
                    if (!this.ifAnyTaskActive()) {
                        this.ifAnyTaskActive.set(true);
                    }
                }
            }
            if (!this.ifAnyTaskActive()) {
                for (let i = 0; i < this.activeTasks.length; i++) {
                    this.viewSubscription[i] = true;
                }
            }
            this.refreshView();
        });
        firstValueFrom(this.generalService.getAccountsForAITask()).then((data: any) => {
            this.accountsNotInAI = data;
            this.refreshView();
        });
    }

    getName(account: Account): string {
        return Account.accountIdent(account);
    }

    getFromDate(element: AITask): string | null {
        return this.datepipe.transform(element.dateEntered, "shortDate");
    }

    switchAccountSelection(pos: number, id: number): void {
        if (this.selectedAccounts[pos].includes(id)) {
            this.selectedAccounts[pos].splice(this.selectedAccounts[pos].indexOf(id), 1);
        } else {
            this.selectedAccounts[pos].push(id);
        }
    }

    selectAllAccounts(pos: number): void {
        this.selectedAll[pos] = !this.selectedAll[pos];
        if (this.selectedAll[pos] && pos % 2 == 0) {
            for (let i = 0; i < this.activeTasks[pos / 2]!.accounts.length; i++) {
                if (!this.selectedAccounts[pos].includes(this.activeTasks[pos / 2]!.accounts[i].id)) {
                    this.selectedAccounts[pos].push(this.activeTasks[pos / 2]!.accounts[i].id);
                }
            }
        } else if (this.selectedAll[pos]) {
            let nAccounts = this.getAccountsNotInAITask(this.activeTasks[(pos-1)/2]!, this.accountsNotInAI);
            for (let i = 0; i < nAccounts.length; i++) {
                if (!this.selectedAccounts[pos].includes(nAccounts[i].id) && (this.activeTasks[(pos - 1) / 2].level == 1 || nAccounts[i].hasFullConsumption)) {
                    this.selectedAccounts[pos].push(nAccounts[i].id);
                }
            }
        } else {
            this.selectedAccounts[pos] = [];
        }
    }

    switchAI(pos: number, level: number) {
        this.generalService.switchAITaskLevel(this.selectedAccounts[pos], level).pipe(first()).subscribe((data: any) => {
            this.refresh();
        });
    }

    getAccountsNotInAITask(aiTask: AITask, accounts: AccountData[]) {
        var res: AccountData[] = [];
        for (let i = 0; i < accounts.length; i++) {
            let found = false;
            for (let j = 0; j < aiTask.accounts.length; j++) {
                if (aiTask.accounts[j].id == accounts[i].id) {
                    found = true;
                    break;
                }
            }
            if (!found) {
                res.push(accounts[i]);
            }
        }
        return res;
    }

    openConsumptionDialog(accountData: AccountData): void {
        this.iConsumptionDialogRef = this.dialog.open(IConsumptionDialog, {
            disableClose: false,
            panelClass: "i-icon-popup"
        });
        this.iConsumptionDialogRef.afterClosed().subscribe(() => {
            this.iConsumptionDialogRef = null!;
        });
        this.iConsumptionDialogRef.componentInstance.updateConsumption.subscribe(() => {
            this.iConsumptionDialogRef = null!;
            this.refresh();
        });
        this.iConsumptionDialogRef.componentInstance.isForm = true;
        this.iConsumptionDialogRef.componentInstance.isAIBased = true;
        this.iConsumptionDialogRef.componentInstance.account = accountData.account;
    }

    changeTaskActivation(): void {
        if (this.ifAnyTaskActive()) {
            //disable all Active Accounts in AITasks
            let aIDs: number[] = [];
            for (let i = 0; i < this.activeTasks.length; i++) {
                for (let j = 0; j < this.activeTasks[i].accounts.length; j++) {
                    aIDs.push(this.activeTasks[i].accounts[j].id);
                }
            }
            if (aIDs.length == 0) {
                this.refresh();
            } else {
                this.generalService.switchAITaskLevel(aIDs, 0).pipe(first()).subscribe((data: any) => {
                    this.ifAnyTaskActive.set(false);
                    this.refresh();
                });
            }
        } else {
            // enable for all accounts level 1 or level 2
            let aIDs1: number[] = [];
            let aIDs2: number[] = [];
            for (let i = 0; i < this.accountsNotInAI.length; i++) {
                if (this.accountsNotInAI[i].hasFullConsumption) {
                    aIDs2.push(this.accountsNotInAI[i].id);
                } else {
                    aIDs1.push(this.accountsNotInAI[i].id);
                }
            }
            var promises = [];
            if (aIDs1.length > 0) {
                promises.push(firstValueFrom(this.generalService.switchAITaskLevel(aIDs1, 1)));
            }
            if (aIDs2.length > 0) {
                promises.push(firstValueFrom(this.generalService.switchAITaskLevel(aIDs2, 2)));
            }
            if (promises.length == 0) {
                this.refresh();
            } else {
                Promise.all(promises).then(x => { this.ifAnyTaskActive.set(true); this.refresh(); });
            }
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}